import React from "react";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";
import { type VariantProp, cv } from "~/tools/class-variance";
import { mergeRefs } from "~/tools/merge-refs";

import { ScrollingContainerContextProvider } from "./_context";
import styles from "./_scrolling-container.module.css";

const variants = cv(
	{},
	{
		variants: {
			scrollX: {
				auto: styles.scrollXAuto,
				visible: styles.scrollXVisible,
				hidden: styles.scrollXHidden,
			},
			scrollY: {
				auto: styles.scrollYAuto,
				visible: styles.scrollYVisible,
				hidden: styles.scrollYHidden,
			},
		},

		defaultVariants: {
			scrollX: "auto",
			scrollY: "auto",
		},
	},
);

const DEFAULT_ELEMENT = "div";

type ScrollXType = VariantProp<typeof variants, "scrollX">;
type ScrollYType = VariantProp<typeof variants, "scrollY">;

interface ScrollingContainerOwnProps {
	/**
	 * @default 'hidden'
	 */
	scrollX?: ScrollXType;

	/**
	 * @default 'auto'
	 */
	scrollY?: ScrollYType;
	className?: string;
}

export type ScrollingContainerProps<TElement extends React.ElementType = typeof DEFAULT_ELEMENT> = PolymorphicBoxProps<
	TElement,
	ScrollingContainerOwnProps
>;

export const ScrollingContainer: <TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
	props: ScrollingContainerProps<TElement>,
) => React.ReactNode = React.forwardRef(
	(
		{
			scrollX = "hidden",
			scrollY = "visible",
			el: Element = DEFAULT_ELEMENT,
			className,
			...props
		}: PolymorphicBoxProps<React.ElementType, ScrollingContainerOwnProps>,
		ref,
	) => {
		const [element, setElement] = React.useState<null | HTMLElement>(null);

		return (
			<ScrollingContainerContextProvider value={React.useMemo(() => ({ element }), [element])}>
				<Element className={variants({ scrollX, scrollY, className })} ref={mergeRefs([ref, setElement])} {...props} />
			</ScrollingContainerContextProvider>
		);
	},
);
